import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from '@/lib/icons';

import {
  createFinancingPlanFormSchema,
  CreateFinancingPlanModel,
} from '@/features/financing-plans/plan';
import { DownPaymentField } from '@/features/financing-plans/DownPaymentField';
import { createFinancingPlan } from '@/features/financing-plans/api';
import { ButtonSpinner } from '@/components/feedback';
import { ApiError } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/_layout/financing-plans/new')({
  component: NewFinancingPlanPage,
});

function NewFinancingPlanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<CreateFinancingPlanModel>({
    resolver: zodResolver(createFinancingPlanFormSchema(t)),
    defaultValues: {
      name: '',
      tenureMonths: 6,
      // Retail price already carries the margin — a plan adds none unless an
      // operator deliberately types one into the field below.
      markupRate: 0,
      downPaymentPercent: 20,
      minCreditTier: 'C',
      maxFinanceAmount: 500,
      isActive: true,
      installmentFrequency: 'daily',
    },
  });

  const onSubmit = async (values: CreateFinancingPlanModel) => {
    try {
      const result = await createFinancingPlan(values);
      if (!result.succeeded) {
        toast({
          title: t('common.error'),
          description:
            result.errors?.[0] ?? t('financingPlans.toast.createFailed'),
          variant: 'destructive',
        });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['financing-plans'] });
      toast({
        title: t('common.success'),
        description: t('financingPlans.toast.created'),
      });
      navigate({ to: '/financing-plans' });
    } catch (error) {
      const description =
        error instanceof ApiError
          ? (error.errors?.[0] ?? error.message)
          : error instanceof Error
            ? error.message
            : t('financingPlans.toast.createFailed');
      toast({
        title: t('common.error'),
        description,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/financing-plans' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">
          {t('financingPlans.form.title')}
        </h1>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>{t('financingPlans.form.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('financingPlans.form.name')}</FormLabel>
                    <FormControl>
                      <Input placeholder="Standard 6-Month Plan" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('financingPlans.form.description')}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="tenureMonths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('financingPlans.form.tenureMonths')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="markupRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('financingPlans.form.markupRate')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DownPaymentField control={form.control} />
                <FormField
                  control={form.control}
                  name="maxFinanceAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('financingPlans.form.maxFinanceAmount')}
                      </FormLabel>
                      <FormControl>
                        {/* Empty means uncapped, which is a real setting the
                            backend honours — not a missing value. */}
                        <Input
                          type="number"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? null
                                : Number(e.target.value)
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        {t('financingPlans.form.maxFinanceHint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="minCreditTier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('financingPlans.form.minCreditTier')}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="A">A - Excellent</SelectItem>
                        <SelectItem value="B">B - Good</SelectItem>
                        <SelectItem value="C">C - Fair</SelectItem>
                        <SelectItem value="D">D - Poor</SelectItem>
                        <SelectItem value="E">E - Very Poor</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="installmentFrequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('financingPlans.form.installmentFrequency')}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <FormLabel className="!mt-0">
                      {t('financingPlans.form.isActive')}
                    </FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting && (
                    <ButtonSpinner className="mr-2" />
                  )}
                  {t('common.actions.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: '/financing-plans' })}
                >
                  {t('common.actions.cancel')}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
